import type { ProductVendor } from "@models/product";
import type { LineRefusal } from "../../_helpers/stockCopy";
import type { LimitedAlternative } from "./lineStock";

export type RefusalNextStep =
    | { kind: "remove" }
    | { kind: "switch"; vendor: ProductVendor }
    | { kind: "lowerToSwitch"; vendor: ProductVendor; lowerTo: number };

/**
 * What a cart line can do after a refused write (#443, PR 326 round 3):
 * - its product, option or sku is gone (`product`): only removal;
 * - its OWN vendor no longer offers it (`offering` on a quantity change): move to the
 *   cheapest other vendor that can take the whole line; else, when a vendor is out of reach
 *   only by the per-line limit, lower that vendor's line so the switch fits, with removal
 *   second (PR 345 review); else removal;
 * - a switch's TARGET no longer offers it (`offering` on a switch): nothing — the line
 *   stays with its own vendor, which still does;
 * - stock: nothing here — the cap, the stock line and its switch cover it;
 * - the per-line limit (`limit`, PR 339): nothing — the message says what to keep under.
 */
export const refusalNextStep = (
    refusal: LineRefusal,
    origin: "quantity" | "switch",
    alternative: ProductVendor | undefined,
    limited?: LimitedAlternative,
): RefusalNextStep | undefined => {
    if (refusal.kind === "product") {
        return { kind: "remove" };
    }

    if (refusal.kind === "offering" && origin === "quantity") {
        if (alternative) {
            return { kind: "switch", vendor: alternative };
        }

        // The other line can come down to 1 at least; at 0 it would have to go entirely,
        // and removing this line is the plainer answer.
        return limited && limited.lowerTo >= 1
            ? { kind: "lowerToSwitch", vendor: limited.vendor, lowerTo: limited.lowerTo }
            : { kind: "remove" };
    }

    return undefined;
};
